import type { ReactNode } from 'react';

import type { Faq } from 'libs/http/api/content/content.types';

import type { RegisMessage } from 'ui/molecules/RegisChat/RegisChat.types';
import type { ComparisonRow } from 'ui/molecules/ComparisonMatrix/ComparisonMatrix.types';
import type { EditorialStep } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection.types';
import type { CoverageCard } from 'ui/molecules/SanctionsCoverage/SanctionsCoverage.types';
import type { ScreeningChainCardProps } from 'ui/molecules/ScreeningChainCard/ScreeningChainCard.types';
import type { FeedAlert } from 'ui/molecules/PepsMonitoringFeed/PepsMonitoringFeed.types';

import styles from './PepsSanctionsPage.module.scss';

type Translate = (key: string) => string;

// ── Hero ────────────────────────────────────────────────────────────────
export const getHeroStats = (t: Translate) => [
  { value: '400+', label: t('Government registries') },
  { value: '600M+', label: t('Company profiles') },
  { value: '200+', label: t('Countries covered') },
];

// ── Signature: interactive group screening ──────────────────────────────
export const getScreening = (t: Translate): Omit<ScreeningChainCardProps, 'className'> => ({
  entityName: 'Meridian Trade FZE',
  entityMeta: t('Screening · entity, directors & UBO chain'),
  query: 'Meridian Trade FZE',
  nodes: [
    {
      id: 'entity',
      name: 'Meridian Trade FZE',
      meta: `${t('United Arab Emirates')} · Reg. 4415207 · ${t('Counterparty')}`,
      scanLabel: t('Screening entity against sanctions lists…'),
      tag: { label: t('NO DIRECT MATCH'), variant: 'green' },
      drawer: [
        { label: t('Lists checked'), value: 'OFAC · EU · UN · OFSI · SECO · DFAT · OSFI · MAS' },
        { label: t('Identity verified'), value: `Reg. 4415207 · ${t('registered address on file')}` },
        { label: t('Result'), value: t('0 matches'), valueTone: 'green' },
      ],
    },
    {
      id: 'holding',
      name: 'Meridian Holdings Ltd',
      meta: `${t('Cyprus')} · HE 402118 · ${t('Owns 68% — verified by registry filing')}`,
      scanLabel: t('Screening 3 directors against PEP data…'),
      tag: { label: t('TIER 3 PEP DIRECTOR'), variant: 'amber' },
      chips: [
        { label: 'A. Christou', variant: 'clear' },
        { label: 'M. Petrov', variant: 'clear' },
        { label: 'D. Volkov', variant: 'pep' },
      ],
      drawer: [
        { label: 'A. Christou', value: t('Clear — verified by DOB'), valueTone: 'green' },
        { label: 'M. Petrov', value: t('Clear — verified by DOB'), valueTone: 'green' },
        { label: 'D. Volkov', value: t('Tier 3 PEP · SOE board member · verified by DOB'), valueTone: 'amber' },
      ],
    },
    {
      id: 'ubo',
      name: 'Northline Capital JSC',
      meta: t('Owns 100% of parent · Listed 2024 · UBO of record'),
      scanLabel: t('Screening ultimate beneficial owner…'),
      tag: { label: 'OFAC SDN', variant: 'red' },
      drawer: [
        { label: t('List entry'), value: `OFAC SDN · ${t('listed 2024')}`, valueTone: 'red' },
        { label: t('Ownership path'), value: `100% → Meridian Holdings → 68% → ${t('counterparty')}` },
        { label: t('Combined sanctioned ownership'), value: t('68% — 50% rule triggered'), valueTone: 'red' },
      ],
    },
  ],
  calc: [
    { text: t('50% rule:') },
    { text: '68%', tone: 'blue' },
    { text: '×', tone: 'muted' },
    { text: '100%', tone: 'blue' },
    { text: '=', tone: 'muted' },
    { text: '68%', tone: 'red' },
    { text: '›', tone: 'muted' },
    { text: t('50% threshold'), tone: 'red' },
  ],
  verdict: {
    body: (
      <>
        <b>{t('50% rule triggered.')}</b>{' '}
        {t(
          'Combined sanctioned ownership of 68% via a two-layer chain. A name-based screen of the counterparty returns clean.',
        )}
      </>
    ),
  },
  sources: `${t('Sources: official registry filings · OFAC SDN list · verified')} 07 Jul 2026`,
  note: t('Click any entity to see the evidence behind the call.'),
  screeningLabel: t('SCREENING'),
  exposedLabel: t('EXPOSED'),
  searchTitle: t('Screen a group structure'),
  searchCta: t('Screen group'),
  searchHint: t('entity · directors · shareholders · UBO chain'),
  replayLabel: t('Replay screening'),
});

// ── Features (numbered editorial) ───────────────────────────────────────
export const getFeatureSteps = (t: Translate): EditorialStep[] => [
  {
    num: '01',
    title: t('Entity-resolved matching'),
    description: t(
      "Every candidate hit is disambiguated against official identifiers — registration number, incorporation date, registered address and, for individuals, date of birth where filed. Common-name noise is discounted automatically instead of landing in an analyst's queue.",
    ),
  },
  {
    num: '02',
    title: t('Ownership-chain screening'),
    description: t(
      'A clean counterparty can still be controlled by a sanctioned entity three layers up. Parents, subsidiaries and ultimate beneficial owners are unfolded from registry filings — not self-reported data — and every one is screened, with 50% rule exposure computed from actual ownership percentages.',
    ),
  },
  {
    num: '03',
    title: t('PEPs linked to their companies'),
    description: t(
      "Tiered PEP classification across 200+ countries, mapped to registry-recorded directorships and shareholdings. You don't just learn a person is politically exposed — you see exactly which entities in your book they own or direct.",
    ),
  },
  {
    num: '04',
    title: t('Evidence on every decision'),
    description: t(
      'Each screening result carries its source list entry, source registry filing, match logic and timestamp. When a regulator asks "how do you know?", the answer is already in the file.',
    ),
  },
];

// ── Comparison ──────────────────────────────────────────────────────────
export const getComparisonRows = (t: Translate): ComparisonRow[] => [
  {
    feature: t('Match basis'),
    us: t('Official identifiers — reg. number, DOB, address'),
    them: t('Fuzzy string similarity'),
  },
  {
    feature: t('Ownership chain screening'),
    us: t('Parents, subsidiaries and UBOs from registry filings'),
    them: t('Counterparty only'),
    themNegative: true,
  },
  {
    feature: t('50% rule exposure'),
    us: t('Computed from registry ownership percentages'),
    them: t('Manual calculation'),
    themNegative: true,
  },
  {
    feature: t('PEP-to-company linkage'),
    us: t('PEPs mapped to registry-recorded directorships and holdings'),
    them: t('Standalone person lists'),
    themNegative: true,
  },
  {
    feature: t('Evidence trail'),
    us: t('Source registry, source list, filing date — audit-ready'),
    them: t('Match score'),
  },
  {
    feature: t('Data provenance'),
    us: t('Primary, government-sourced — 400+ registries, 200+ countries'),
    them: t('Mixed third-party sources'),
  },
];

// ── Coverage ────────────────────────────────────────────────────────────
export const getCoverageCards = (t: Translate): CoverageCard[] => [
  {
    variant: 'lists',
    title: t('Sanctions lists'),
    sub: t('Consolidated and refreshed continuously from official issuing bodies.'),
    rows: [
      { code: 'us', label: t('OFAC — United States'), desc: t('SDN and Consolidated (Non-SDN) lists, US Treasury') },
      { code: 'eu', label: t('European Union'), desc: t('Consolidated Financial Sanctions List') },
      { icon: 'globe-1', label: t('United Nations'), desc: t('Security Council Consolidated List') },
      { code: 'gb', label: t('UK — OFSI / HMT'), desc: t('HM Treasury Consolidated List') },
      {
        icon: 'government',
        label: t('National regimes'),
        desc: t('SECO, DFAT, OSFI, MAS and other jurisdiction lists'),
      },
      {
        icon: 'compliance-1',
        label: t('Sectoral & enforcement'),
        desc: t('Sectoral programmes and regulatory enforcement lists'),
      },
    ],
  },
  {
    variant: 'tiers',
    title: t('PEP data'),
    sub: t('Tiered classification across 200+ countries — linked to registry-recorded positions.'),
    tiers: [
      { badge: 'T1', tone: 't1', text: t('Heads of state & national government') },
      { badge: 'T2', tone: 't2', text: t('Regional government & senior officials') },
      { badge: 'T3', tone: 't3', text: t('State-owned enterprise executives & boards') },
      { badge: 'RCA', tone: 't4', text: t('Relatives & close associates') },
    ],
    notes: [t('Current & former status, with dates'), t('Mapped to directorships & holdings')],
  },
  {
    variant: 'stats',
    title: t('The identity layer'),
    sub: t('What makes the matching work — the registry data underneath.'),
    stats: [
      { value: '600M+', label: t('company profiles with directors, shareholders & UBOs') },
      { value: '400+', label: t('government registries, retrieved at the source') },
      { value: '200+', label: t('countries with ownership & officer coverage') },
      { value: '100%', label: t('primary, government-sourced — straight from official filings') },
    ],
  },
];

// ── Monitoring ──────────────────────────────────────────────────────────
export const getMonitoringBullets = (t: Translate) => [
  t('New list additions'),
  t('Ownership changes — 50% rule recomputed'),
  t('New PEP appointments'),
  t('Webhook, email or platform delivery'),
];

export const MONITORING_WATCHING = 2340;

export const getMonitoringAlerts = (t: Translate): FeedAlert[] => [
  {
    id: 'sanctions',
    tone: 'red',
    glyph: '!',
    category: t('SANCTIONS'),
    title: t('Northline Capital JSC added to OFAC SDN'),
    description: t('3 portfolio entities now carry indirect exposure via ownership — 50% rule recomputed'),
    time: '2m',
    drawer: [
      { label: t('Source'), value: `OFAC SDN ${t('entry · listed 2024')}` },
      { label: t('Affected entities'), value: `Meridian Trade FZE ${t('+ 2 others')}` },
      { label: t('Combined sanctioned ownership'), value: t('68% — 50% rule triggered'), valueTone: 'red' },
    ],
  },
  {
    id: 'ownership',
    tone: 'amber',
    glyph: 'Δ',
    category: t('OWNERSHIP'),
    title: t('Ownership change — Meridian Holdings Ltd'),
    description: t('Stake in Meridian Trade FZE increased 44% → 68%, per new Cyprus registry filing'),
    time: '1h',
    drawer: [
      { label: t('Source'), value: `${t('Cyprus registry')} · HE 402118 · ${t('filed')} 07 Jul 2026` },
      { label: t('Change'), value: t('44% → 68% shareholding') },
      { label: t('Exposure'), value: t('50% rule recomputed across chain'), valueTone: 'amber' },
    ],
  },
  {
    id: 'pep',
    tone: 'amber',
    glyph: '◆',
    category: t('PEP STATUS'),
    title: t('New PEP appointment detected'),
    description: t('D. Volkov appointed to a state-owned enterprise board — Tier 3 classification applied'),
    time: '6h',
    drawer: [
      { label: t('Source'), value: t('Registry appointment record · verified by DOB') },
      { label: t('Classification'), value: t('Tier 3 — SOE board member'), valueTone: 'amber' },
      { label: t('Linked entity'), value: 'Meridian Holdings Ltd' },
    ],
  },
  {
    id: 'list',
    tone: 'green',
    glyph: '✓',
    category: t('LIST UPDATE'),
    title: t('EU Consolidated list updated'),
    description: t('Portfolio re-screened against 38 new entries — no new matches'),
    time: '1d',
    drawer: [
      { label: t('New list entries'), value: '38' },
      { label: t('Entities re-screened'), value: '2,340' },
      { label: t('Result'), value: t('0 new matches'), valueTone: 'green' },
    ],
  },
];

// ── Regis AI ────────────────────────────────────────────────────────────
export const getRegisBullets = (t: Translate) => [
  t('Natural language screening across 600M+ company profiles'),
  t('Every answer cites the source registry and source list'),
  t('Batch screening and ongoing monitoring via API and webhooks'),
  t('Available in the platform, Excel and the Global Database MCP connector'),
];

const Cite = ({ children }: { children: ReactNode }) => <span className={styles.cite}>{children}</span>;

export const getRegisMessages = (t: Translate): RegisMessage[] => [
  {
    from: 'user',
    content: t('Is Meridian Trade FZE, its directors or UBOs sanctioned?'),
  },
  {
    from: 'assistant',
    content: (
      <>
        {t('No direct match on the entity — but')} <b>{t('indirect exposure found.')}</b>{' '}
        {t(
          'Meridian Trade FZE is 68% owned by Meridian Holdings Ltd (Cyprus), which is wholly owned by Northline Capital JSC, listed on the',
        )}{' '}
        <b>{t('OFAC SDN list')}</b> {t('since 2024. The')} <b>{t('50% rule is triggered')}</b>{' '}
        {t('at 68% combined sanctioned ownership. One director of the intermediate holding is a')}{' '}
        <b>{t('Tier 3 PEP')}</b> {t('(SOE board member), verified by date of birth.')}
        <span className={styles.cites}>
          <Cite>{`${t('UAE registry')} · Reg. 4415207`}</Cite>
          <Cite>{`${t('Cyprus registry')} · HE 402118`}</Cite>
          <Cite>{`OFAC SDN · ${t('2024 entry')}`}</Cite>
        </span>
      </>
    ),
  },
];

// ── FAQ ─────────────────────────────────────────────────────────────────
export const getFaqItems = (t: Translate): Faq[] => [
  {
    id: 'faq-1',
    default_opened: true,
    question: t('How do you reduce false positives compared to our current tool?'),
    answer: t(
      'Every candidate match is scored against official identifiers from the registry record — registration number, incorporation date, registered address and, for individuals, date of birth where filed. A "John Smith" hit that can\'t be reconciled with the record is discounted automatically instead of landing in an analyst\'s queue.',
    ),
  },
  {
    id: 'faq-2',
    question: t('How is the 50% rule handled?'),
    answer: t(
      'Ownership percentages come from registry filings, so combined sanctioned ownership is computed across the full chain — including exposure split across multiple sanctioned holders. Indirect exposure is flagged with the exact chain and percentages, ready for your escalation file.',
    ),
  },
  {
    id: 'faq-3',
    question: t('Where does the data come from?'),
    answer: t(
      'Company, officer and ownership data is primary and government-sourced, from 400+ government registries across 200+ countries. Sanctions data is consolidated from the official issuing bodies. Every data point carries its source and date.',
    ),
  },
  {
    id: 'faq-4',
    question: t('Will this stand up to an audit or regulatory examination?'),
    answer: t(
      'Every screening decision includes its evidence: the source list entry, the source registry filing, the match logic applied and a timestamp. Your audit file writes itself as your team works.',
    ),
  },
  {
    id: 'faq-5',
    question: t('How does ongoing monitoring work?'),
    answer: t(
      "Your portfolio is re-screened continuously. New list additions, changes in ownership structure and new PEP appointments among directors or UBOs generate alerts via webhook, email or the platform — so a counterparty that was clean at onboarding doesn't stay unwatched.",
    ),
  },
  {
    id: 'faq-6',
    question: t('Can we test it against our own book?'),
    answer: t(
      'Yes. Bring a sample of your counterparties to the demo — including ones your current tool cleared — and see what registry-resolved screening surfaces.',
    ),
  },
];
